import { useMemo, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  RiEyeLine,
  RiDownloadLine,
  RiSendPlane2Line,
  RiAlarmSnoozeLine,
  RiCloseLine,
  RiSaveLine,
  RiHourglass2Line,
} from "@remixicon/react";
import { pdf } from "@react-pdf/renderer";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/Drawer";
import { useAuthStore } from "@store/authStore";
import { normalizeRole, ROLES } from "@/lib/permissions";
import TripSheetDocument from "./TripSheetDocument";
import TripSheetPdf from "./TripSheetPdf";
import { getAircraftModelName, getAircraftPhotos, probeImageUrl, getEffectiveAircraft } from "@/features/tails/utils";
import { apiClient } from "@/lib/api";
import { useAirportsByIcao } from "@/features/quotes/services/airportService";
import { formatDateInAirportTimezone, formatTimeInAirportTimezone, getAirportTimezone } from "@/utils/airportTimezone";
import { buildFinalizePassengerProfilesEmailHtml, buildBookingSecuredEmailHtml } from "@/utils/emailTemplates";
import { getDistance } from "geolib";
import { resolveAssetUrl } from "@/utils/urls";
import { captureRouteMap } from "../services/routeMap";
import { patchQuoteFiles as patchQuoteFilesSvc } from "@/features/quotes/services/quoteService";
import html2canvas from "html2canvas";
import { TrustJetPlane, getAircraftImage, getAircraftBundledCarouselUrls } from "@assets/images";

/**
 * Snapshot the drawer's already-rendered map card (tagged with
 * data-tripsheet-map on AircraftMediaPreview) directly, so the PDF and the
 * drawer use the exact same map — one tile fetch, guaranteed pixel parity.
 * Falls back to null so the caller can invoke captureRouteMap as a fallback.
 */
const captureDrawerMap = async () => {
  const el = document.querySelector('[data-tripsheet-map]');
  if (!el) return null;
  try {
    const canvas = await html2canvas(el, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#FFFFFF',
      logging: false,
      // scale:2 supersamples so the JPEG stays sharp when the PDF viewer
      // downsamples it into the ~190×120 media card.
      scale: 2,
    });
    return canvas.toDataURL('image/jpeg', 0.95);
  } catch (err) {
    console.warn('captureDrawerMap failed, falling back to off-screen map:', err);
    return null;
  }
};

/**
 * Rebuild the flights[] array from the edited documentData.
 *
 * documentData stores each leg's departure as a localized display pair —
 * date like "Apr 30, 2026" and time like "09:44" — in the airport's timezone.
 * To produce the canonical `departure_date` the backend expects
 * (e.g. "2026-04-30T13:44:00.000Z"), we parse the pair back through
 * moment-timezone using the airport's IANA zone and emit a UTC ISO string.
 *
 * Any fields not represented in documentData (flight_id, aircraft_id, etc.)
 * are carried over from the original flights[] entry at the same index.
 */
const buildUpdatedFlightsFromDocument = (documentData, originalFlights = []) => {
  if (!documentData?.legs) return originalFlights;
  const moment = require('moment-timezone');
  return documentData.legs.map((leg, idx) => {
    const base = originalFlights[idx] || {};
    const departureCode = leg.departure?.code || base.departure_airport || '';
    const arrivalCode = leg.arrival?.code || base.arrival_airport || '';

    let departureIso = base.departure_date || null;
    if (leg.departure?.date && leg.departure?.time) {
      const tz = getAirportTimezone(departureCode);
      const parsed = moment.tz(
        `${leg.departure.date} ${leg.departure.time}`,
        ['MMM D, YYYY HH:mm', 'MMMM D, YYYY HH:mm'],
        tz,
      );
      if (parsed.isValid()) departureIso = parsed.toISOString();
    }

    return {
      ...base,
      departure_airport: departureCode,
      arrival_airport: arrivalCode,
      departure_date: departureIso,
    };
  });
};

const TripSheetPreviewDrawer = ({
  isOpen,
  onClose,
  quoteData,
  acceptedBid,
  aircraftDetails,
  organization,
  flights,
  passengers,
  crew,
  services,
  fbos = [],
  // findAirport is intentionally omitted from the props now — the drawer
  // batches its own ICAO lookups from the `flights` prop via
  // useAirportsByIcao. If the parent still passes it, JSX just ignores it.
  requestClientPassengers = false,
  hasFlight = false
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Collect every ICAO on this trip's legs and fire batched exact lookups.
  // React Query dedupes per code across the whole tree, so if TripSheetPage
  // already fetched the same codes they resolve instantly here.
  const flightIcaos = useMemo(() => {
    const codes = [];
    (flights || []).forEach((f) => {
      if (f?.departure_airport) codes.push(f.departure_airport);
      if (f?.arrival_airport) codes.push(f.arrival_airport);
    });
    return codes;
  }, [flights]);
  const { byIcao: airportByIcao } = useAirportsByIcao(flightIcaos);
  // Local synchronous lookup — matches the old `findAirport` signature so
  // the callsites below don't have to change.
  const findAirport = useCallback(
    (icao) => (icao ? airportByIcao.get(String(icao).toUpperCase()) || null : null),
    [airportByIcao],
  );
  // junior_broker, finance, and compliance_qa can't dispatch the trip sheet
  // to the client — hide the Send button entirely for them.
  const { userProfile } = useAuthStore();
  const role = normalizeRole(userProfile?.role);
  const canSendTripSheet =
    role !== ROLES.JUNIOR_BROKER &&
    role !== ROLES.FINANCE &&
    role !== ROLES.COMPLIANCE_QA;
  // junior_broker can't Send, so they need a Save Draft button that
  // persists the drawer edits (client, passengers, crew, services, FBOs,
  // flight legs) without generating a PDF, uploading to S3, or emailing
  // the client. Other roles have Send Trip Sheet which already saves.
  const canSaveDraftOnly = role === ROLES.JUNIOR_BROKER;

  // Build initial document data from props
  const initialData = useMemo(() => {
    const clientNameVal = [quoteData?.client?.first_name, quoteData?.client?.last_name]
      .filter(Boolean)
      .join(" ");

    const legs = flights.map((flight, idx) => {
      const depAirport = findAirport?.(flight.departure_airport);
      const arrAirport = findAirport?.(flight.arrival_airport);
      const depDate = flight.departure_date ? flight.departure_date : null;
      const legFbo = fbos[idx] || {};
      const depAirportAddr = typeof depAirport?.address === "object" ? depAirport?.address?.street || "" : depAirport?.address || "";
      const arrAirportAddr = typeof arrAirport?.address === "object" ? arrAirport?.address?.street || "" : arrAirport?.address || "";

      return {
        legNumber: idx + 1,
        timezone: "",
        flightDistance: "",
        flightDuration: "",
        departure: {
          code: flight.departure_airport || "",
          airportName: depAirport?.name || "",
          fbo: legFbo.departure?.name || "",
          // Prefer the FBO's address; fall back to the airport address (the
          // async airport-data effect fills this when both are empty).
          address: legFbo.departure?.address || depAirportAddr,
          contactNumber: depAirport?.phone || "",
          // Coords needed for the trailing route-map preview + PDF capture.
          // findAirport is synchronous (cached useAirports query), so these
          // are usually populated on first render — the async airport fetch
          // overrides them later if a fresher record comes back.
          latitude: depAirport?.latitude ?? depAirport?.lat ?? null,
          longitude: depAirport?.longitude ?? depAirport?.lng ?? depAirport?.lon ?? null,
          weather: "",
          tempHi: "",
          tempLo: "",
          time: depDate ? formatTimeInAirportTimezone(depDate, flight.departure_airport) : "",
          date: depDate ? formatDateInAirportTimezone(depDate, flight.departure_airport, { year: "numeric" }) : "",
          day: depDate ? formatDateInAirportTimezone(depDate, flight.departure_airport, { weekday: "long", day: undefined, month: undefined }) : "",
        },
        arrival: {
          code: flight.arrival_airport || "",
          airportName: arrAirport?.name || "",
          fbo: legFbo.arrival?.name || "",
          address: legFbo.arrival?.address || arrAirportAddr,
          contactNumber: arrAirport?.phone || "",
          latitude: arrAirport?.latitude ?? arrAirport?.lat ?? null,
          longitude: arrAirport?.longitude ?? arrAirport?.lng ?? arrAirport?.lon ?? null,
          weather: "",
          tempHi: "",
          tempLo: "",
          time: "",
          date: depDate ? formatDateInAirportTimezone(depDate, flight.arrival_airport, { year: "numeric" }) : "",
          day: depDate ? formatDateInAirportTimezone(depDate, flight.arrival_airport, { weekday: "long", day: undefined, month: undefined }) : "",
        },
        crew: (crew || []).map((c) => ({ name: c.name || "", phone: c.phone || "" })),
        passengers: [...(passengers || [])],
        // Broker's per-leg operational note from Flight Details. Same
        // source the itinerary email reads — leg.notes first with a
        // baggage_requirements fallback for rows saved by the legacy
        // writer. Trimmed so a whitespace-only value doesn't render a
        // Note card in the crew/passenger row.
        notes: (flight.notes || flight.baggage_requirements || "").trim(),
        additionalServices: (services || []).map((s) => ({
          name: s.name,
          serviceDetails: s.details || "",
          address: "",
          phone: "",
          confirmationNumber: "",
        })),
      };
    });

    return {
      // Header "brokerage block" in the trip sheet (top-right). Schema
      // key is `operator` for historical reasons — the subtree carries
      // BROKERAGE details (name / address / phone), matching the
      // brokerage logo on the left side of the same header. The
      // per-quote operator-name override
      // (quote.files.operator_name_overrides[aircraftId]) still powers
      // the operator identity everywhere else via
      // getEffectiveOperatorName in src/features/tails/utils.js — it
      // just doesn't belong in this block.
      operator: {
        name: organization?.name || "",
        address: typeof organization?.address === "object" ? organization?.address?.street || "" : organization?.address || "",
        phone: organization?.phone || "",
        fax: "",
      },
      client: {
        name: clientNameVal,
        company: quoteData?.client?.company_name || "",
        email: quoteData?.client?.email || "",
        phone: quoteData?.client?.phone || "",
      },
      aircraft: (() => {
        // Trip sheet re-fetches aircraft via getAircraftById in the parent,
        // so aircraftDetails is the base record — merge the per-quote
        // override here so tail number / photos / amenities reflect what
        // the broker saved for THIS quote (quote.files.aircraft_overrides).
        const mergedAircraft = getEffectiveAircraft(aircraftDetails, quoteData);
        return {
          image: getAircraftPhotos(mergedAircraft),
          tailNumber: mergedAircraft?.tail_number || "",
          name: getAircraftModelName(mergedAircraft),
          makeModel: mergedAircraft?.aircraft_make_model?.make_model || mergedAircraft?.make_model || "",
          passengerCount: String(acceptedBid?.passenger_count || ""),
          bannerImageUrl: mergedAircraft?.banner_image_url || null,
          imageUrls: mergedAircraft?.image_urls || [],
          // Aircraft Note — broker's per-aircraft note (custom_profile
          // extras or owned-aircraft extra_attributes.notes, unified via
          // getEffectiveAircraft.quote_notes). Rendered as the amber
          // callout in the drawer preview + trip-sheet page.
          note: mergedAircraft?.quote_notes || "",
        };
      })(),
      legs,
    };

    
  }, [quoteData, acceptedBid, aircraftDetails, organization, flights, passengers, crew, services, fbos, findAirport]);

  // Local editable state for the document
  const [documentData, setDocumentData] = useState(null);
  const [protectedCounts, setProtectedCounts] = useState(null);
  const [fetchingArrivalTimes, setFetchingArrivalTimes] = useState(false);
  const [fetchingAirportData, setFetchingAirportData] = useState(false);

  // Sync local state when drawer opens or initial data changes, then fetch arrival times
  useEffect(() => {
    if (isOpen && initialData) {
      setDocumentData(JSON.parse(JSON.stringify(initialData)));
      // Store initial counts so pre-populated items can't be removed
      setProtectedCounts({
        legs: initialData.legs.map((leg) => ({
          passengers: leg.passengers.length,
          crew: leg.crew.length,
          services: leg.additionalServices.length,
        })),
      });

      // Fetch arrival times from LLM API for each leg
      if (flights.length) {
        const fetchArrivalTimes = async () => {
          setFetchingArrivalTimes(true);
          const updates = await Promise.allSettled(
            flights.map((flight) => {
              const depTime = formatTimeInAirportTimezone(flight.departure_date, flight.departure_airport, { hour12: true, timeZoneName: 'short' });
              return apiClient.getArrivalTime({
                from_location: flight.departure_airport,
                to_location: flight.arrival_airport,
                departure_time: depTime,
              });
            })
          );

          setDocumentData((prev) => {
            if (!prev) return prev;
            const moment = require('moment-timezone');
            const next = JSON.parse(JSON.stringify(prev));
            updates.forEach((result, idx) => {
              if (result.status === "fulfilled" && result.value?.arrival_time) {
                // Convert "09:16 PM EST" → "21:16" (24-hour format)
                const raw = result.value.arrival_time;
                const match = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                if (match) {
                  let h = parseInt(match[1], 10);
                  const m = match[2];
                  const period = match[3].toUpperCase();
                  if (period === "PM" && h !== 12) h += 12;
                  if (period === "AM" && h === 12) h = 0;
                  const arrivalTime24h = `${String(h).padStart(2, "0")}:${m}`;
                  next.legs[idx].arrival.time = arrivalTime24h;

                  // Correct arrival date if flight crosses midnight
                  const flight = flights[idx];
                  if (flight?.departure_date) {
                    const arrTz = getAirportTimezone(flight.arrival_airport);
                    // Get departure date as seen in the arrival timezone
                    const depInArrTz = moment.utc(flight.departure_date).tz(arrTz);
                    const arrDateStr = depInArrTz.format('YYYY-MM-DD');
                    // Construct arrival datetime in arrival timezone
                    const arrMoment = moment.tz(`${arrDateStr} ${arrivalTime24h}`, 'YYYY-MM-DD HH:mm', arrTz);
                    // If arrival is before departure (UTC), it must be the next day
                    if (arrMoment.valueOf() < moment.utc(flight.departure_date).valueOf()) {
                      arrMoment.add(1, 'day');
                    }
                    const arrivalDate = arrMoment.toDate();
                    next.legs[idx].arrival.date = formatDateInAirportTimezone(arrivalDate, flight.arrival_airport, { year: "numeric" });
                    next.legs[idx].arrival.day = formatDateInAirportTimezone(arrivalDate, flight.arrival_airport, { weekday: "long", day: undefined, month: undefined });

                    // Compute flight duration from departure UTC to arrival UTC
                    const durationMin = Math.round((arrMoment.valueOf() - moment.utc(flight.departure_date).valueOf()) / 60000);
                    if (durationMin > 0) {
                      const hrs = Math.floor(durationMin / 60);
                      const mins = durationMin % 60;
                      next.legs[idx].flightDuration = `${hrs}h ${String(mins).padStart(2, "0")}m`;
                    }
                  }
                } else {
                  next.legs[idx].arrival.time = raw;
                }
              }
            });
            return next;
          });
          setFetchingArrivalTimes(false);
        };

        fetchArrivalTimes();

        // Fetch timezone and flight distance from airport API for each leg
        const fetchAirportData = async () => {
          setFetchingAirportData(true);
          const results = await Promise.allSettled(
            flights.map(async (flight) => {
              const [dep, arr] = await Promise.all([
                apiClient.getAirportByIcao(flight.departure_airport),
                apiClient.getAirportByIcao(flight.arrival_airport),
              ]);
              return { dep, arr };
            })
          );

          setDocumentData((prev) => {
            if (!prev) return prev;
            const next = JSON.parse(JSON.stringify(prev));
            results.forEach((result, idx) => {
              if (result.status !== "fulfilled") return;
              const { dep, arr } = result.value;
              if (dep?.time_zone) {
                next.legs[idx].timezone = dep.time_zone;
              }
              if (dep?.latitude && dep?.longitude && arr?.latitude && arr?.longitude) {
                const distanceKm = getDistance(
                  { latitude: dep.latitude, longitude: dep.longitude },
                  { latitude: arr.latitude, longitude: arr.longitude },
                ) / 1000;
                const distanceNm = Math.round(distanceKm * 0.539957);
                next.legs[idx].flightDistance = `${distanceNm} NM`;
              }
              // Populate address from airport API data if not already set
              if (dep) {
                if (!next.legs[idx].departure.address) {
                  next.legs[idx].departure.address = [dep.city, dep.state, dep.country].filter(Boolean).join(", ");
                }
                if (!next.legs[idx].departure.airportName && dep.name) {
                  next.legs[idx].departure.airportName = dep.name;
                }
                // Persist coordinates for the trailing route-map render.
                if (typeof dep.latitude === 'number') next.legs[idx].departure.latitude = dep.latitude;
                if (typeof dep.longitude === 'number') next.legs[idx].departure.longitude = dep.longitude;
              }
              if (arr) {
                if (!next.legs[idx].arrival.address) {
                  next.legs[idx].arrival.address = [arr.city, arr.state, arr.country].filter(Boolean).join(", ");
                }
                if (!next.legs[idx].arrival.airportName && arr.name) {
                  next.legs[idx].arrival.airportName = arr.name;
                }
                if (typeof arr.latitude === 'number') next.legs[idx].arrival.latitude = arr.latitude;
                if (typeof arr.longitude === 'number') next.legs[idx].arrival.longitude = arr.longitude;
              }
            });
            return next;
          });
          setFetchingAirportData(false);
        };

        fetchAirportData();
      }
    }
  }, [isOpen, initialData]);

  // Patch leg airport codes and departure date/time whenever the parent's
  // editable flights change, so edits on TripSheetPage reflect immediately
  // in the preview without resetting other in-drawer edits.
  useEffect(() => {
    setDocumentData(prev => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      flights.forEach((flight, idx) => {
        if (!next.legs[idx]) return;
        const depAirport = findAirport?.(flight.departure_airport);
        const arrAirport = findAirport?.(flight.arrival_airport);
        const depDate = flight.departure_date || null;
        const prevDepCode = next.legs[idx].departure.code;
        next.legs[idx].departure.code = flight.departure_airport || prevDepCode;
        if (flight.departure_airport && flight.departure_airport !== prevDepCode) {
          next.legs[idx].departure.fbo = '';
          next.legs[idx].departure.address = '';
          next.legs[idx].departure.airportName = depAirport?.name || '';
        } else if (depAirport?.name) {
          next.legs[idx].departure.airportName = depAirport.name;
        }

        const prevArrCode = next.legs[idx].arrival.code;
        next.legs[idx].arrival.code = flight.arrival_airport || prevArrCode;
        if (flight.arrival_airport && flight.arrival_airport !== prevArrCode) {
          next.legs[idx].arrival.fbo = '';
          next.legs[idx].arrival.address = '';
          next.legs[idx].arrival.airportName = arrAirport?.name || '';
        } else if (arrAirport?.name) {
          next.legs[idx].arrival.airportName = arrAirport.name;
        }
        if (depDate) {
          next.legs[idx].departure.time = formatTimeInAirportTimezone(depDate, flight.departure_airport);
          next.legs[idx].departure.date = formatDateInAirportTimezone(depDate, flight.departure_airport, { year: 'numeric' });
          next.legs[idx].departure.day = formatDateInAirportTimezone(depDate, flight.departure_airport, { weekday: 'long', day: undefined, month: undefined });
          next.legs[idx].arrival.date = formatDateInAirportTimezone(depDate, flight.arrival_airport, { year: 'numeric' });
          next.legs[idx].arrival.day = formatDateInAirportTimezone(depDate, flight.arrival_airport, { weekday: 'long', day: undefined, month: undefined });
        }
      });
      return next;
    });
  }, [flights, findAirport]);

  // Path-based onChange handler (e.g. "legs.0.crew.1.name")
  const handleChange = useCallback((path, value) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let target = next;
      for (let i = 0; i < keys.length - 1; i++) {
        target = target[keys[i]];
      }
      target[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const handleAddPassenger = useCallback((legIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].passengers.push("");
      return next;
    });
  }, []);

  const handleRemovePassenger = useCallback((legIdx, pIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].passengers.splice(pIdx, 1);
      return next;
    });
  }, []);

  const handleAddCrew = useCallback((legIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].crew.push({ name: "", phone: "" });
      return next;
    });
  }, []);

  const handleRemoveCrew = useCallback((legIdx, cIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].crew.splice(cIdx, 1);
      return next;
    });
  }, []);

  const handleAddService = useCallback((legIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].additionalServices.push({
        name: "",
        serviceDetails: "",
        address: "",
        phone: "",
        confirmationNumber: "",
      });
      return next;
    });
  }, []);

  const handleRemoveService = useCallback((legIdx, sIdx) => {
    setDocumentData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      next.legs[legIdx].additionalServices.splice(sIdx, 1);
      return next;
    });
  }, []);

  const tripTitle = useMemo(() => {
    if (!flights.length) return "Trip Sheet";
    const dep = flights[0]?.departure_airport || "";
    const arr = flights[0]?.arrival_airport || "";
    return `${dep} - ${arr} Trip`;
  }, [flights]);

  const clientName = useMemo(() => {
    return [quoteData?.client?.first_name, quoteData?.client?.last_name]
      .filter(Boolean)
      .join(" ");
  }, [quoteData]);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const fetchImageAsBase64 = useCallback(async (src, options = {}) => {
    const { maxWidth, jpegQuality = 0.82 } = options;
    let imgSrc = src;
    // Unwrap webpack module objects (e.g. { default: '/static/media/...' })
    if (imgSrc && typeof imgSrc === "object" && imgSrc.default) {
      imgSrc = imgSrc.default;
    }
    if (!imgSrc || typeof imgSrc !== "string" || imgSrc.startsWith("data:")) {
      return imgSrc || null;
    }
    try {
      // For local paths (e.g. /static/media/...), prepend the current origin
      const fetchUrl = imgSrc.startsWith("http") ? imgSrc : window.location.origin + imgSrc;
      // cache: 'no-store' — always hit the network, never read a stale
      // cached response. Prevents the classic CORS-was-fixed-but-browser-
      // still-serves-the-old-headerless-response bug when the S3 bucket's
      // CORS config changes between sessions.
      const resp = await fetch(fetchUrl, { cache: 'no-store' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();

      // Downscale + recompress when maxWidth is provided. Keeps the PDF lean
      // by avoiding embedded full-resolution JetPhotos source images.
      if (maxWidth) {
        const objectUrl = URL.createObjectURL(blob);
        try {
          const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = reject;
            el.src = objectUrl;
          });
          const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
          const targetW = Math.round(img.width * ratio);
          const targetH = Math.round(img.height * ratio);
          const canvas = document.createElement("canvas");
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, targetW, targetH);
          return canvas.toDataURL("image/jpeg", jpegQuality);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      }

      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error("Failed to fetch image for PDF:", imgSrc, e);
      return null;
    }
  }, []);

  const resolvePdfData = useCallback(async (data) => {
    // Per-image fallback chain — mirror the UI's <AircraftImage>:
    //   primary URL → make_model bundled image → TrustJetPlane.
    // fetchImageAsBase64 returns null on network/CORS/404 (it catches its own
    // errors), so we can chain with `??`-style sequential awaits.
    const makeModel = data.aircraft?.makeModel;
    const modelBundled = makeModel ? getAircraftImage(makeModel) : null;
    // Probe each URL before the expensive fetchImageAsBase64 — probing uses
    // new Image() (no CORS preflight, no base64 conversion, no console 404
    // noise from fetch itself) so we don't hammer known-broken URLs with a
    // full download just to reject the response.
    const fetchWithFallback = async (primarySrc, options) => {
      if (primarySrc && (await probeImageUrl(primarySrc))) {
        const r = await fetchImageAsBase64(primarySrc, options);
        if (r) return r;
      }
      if (modelBundled && (await probeImageUrl(modelBundled))) {
        const r = await fetchImageAsBase64(modelBundled, options);
        if (r) return r;
      }
      return fetchImageAsBase64(TrustJetPlane, options);
    };

    // Resolve aircraft image — downscale to keep the PDF small (rendered ~200px wide).
    const aircraftImage = await fetchWithFallback(data.aircraft.image, {
      maxWidth: 600,
      jpegQuality: 0.8,
    });

    // Resolve operator logo
    let logoSrc = null;
    if (organization?.branding?.logo) {
      logoSrc = await fetchImageAsBase64(resolveAssetUrl(organization.branding.logo));
    }

    // ---------------------------------------------------------------------
    // Trailing "aircraft media" row (last page): [ route map | exterior | interior ]
    // Mirrors mobile's generateAircraftMediaHtml — same URL ordering
    // (banner_image_url first, then image_urls, deduped) and the same
    // three-card 33/33/33 layout at 120pt tall.
    // ---------------------------------------------------------------------

    // Aircraft media URLs — banner_image_url first, then image_urls (deduped).
    // We probe each in parallel via fetchImageAsBase64 (returns null on failure)
    // and keep only the ones that actually loaded. Up to two are shown; if
    // none load, a single make_model bundled image is used instead of two
    // duplicate cards.
    const rawMediaUrls = [];
    if (data.aircraft?.bannerImageUrl) rawMediaUrls.push(data.aircraft.bannerImageUrl);
    (data.aircraft?.imageUrls || []).forEach((u) => {
      if (u && !rawMediaUrls.includes(u)) rawMediaUrls.push(u);
    });

    // Fetch + downscale exterior + interior + map in parallel — they're
    // independent so wall-clock ≈ slowest single fetch.
    // Flexible coord extraction — matches AircraftMediaPreview.extractCoord.
    // Some backends return coords as strings (e.g. "40.6924"), so a strict
    // typeof === 'number' check silently drops all of them.
    const toCoord = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      const lat = obj.latitude ?? obj.lat;
      const lng = obj.longitude ?? obj.lng ?? obj.lon;
      const nLat = typeof lat === 'string' ? parseFloat(lat) : lat;
      const nLng = typeof lng === 'string' ? parseFloat(lng) : lng;
      if (Number.isFinite(nLat) && Number.isFinite(nLng)) return { lat: nLat, lng: nLng };
      return null;
    };

    const airportPoints = [];
    const seen = new Set();
    for (const leg of data.legs || []) {
      for (const key of ['departure', 'arrival']) {
        const raw = leg?.[key];
        const coord = toCoord(raw);
        if (!coord) continue;
        const id = `${raw?.code || ''}:${coord.lat}:${coord.lng}`;
        if (seen.has(id)) continue;
        seen.add(id);
        airportPoints.push({ ...coord, icao: raw?.code });
      }
    }
    // eslint-disable-next-line no-console
    console.log('[resolvePdfData] airportPoints for capture:', airportPoints);

    // Map capture: try the drawer's already-rendered map first (one map, one
    // tile fetch, pixel parity with what the broker previewed). Falls back
    // to the off-screen Leaflet mount if the drawer node isn't in the DOM.
    const mapImagePromise = (async () => {
      if (airportPoints.length === 0) return null;
      const fromDrawer = await captureDrawerMap();
      if (fromDrawer) return fromDrawer;
      return captureRouteMap(airportPoints, { width: 400, height: 300 });
    })();

    // Probe each URL first (cheap new Image() load — no CORS preflight, no
    // base64 conversion) so we only run the expensive fetchImageAsBase64
    // step on URLs that will actually succeed. Otherwise the PDF generator
    // hammers each broken S3 URL with a full CORS-preflighted fetch just
    // to throw the response away.
    const resolvedMediaUrls = rawMediaUrls.map(resolveAssetUrl);
    const [mediaProbed, mapImage] = await Promise.all([
      Promise.all(resolvedMediaUrls.map((u) => probeImageUrl(u))),
      mapImagePromise,
    ]);
    const workingMediaUrls = resolvedMediaUrls
      .filter((_, i) => mediaProbed[i])
      .slice(0, 2);
    const mediaFetched = await Promise.all(
      workingMediaUrls.map((u) => fetchImageAsBase64(u, { maxWidth: 600, jpegQuality: 0.7 })),
    );
    // Keep only images that actually loaded (fetch can still fail on a
    // race — cache eviction, timeout — even after a successful probe).
    const validMedia = mediaFetched.filter(Boolean);
    // Fallback: if no aircraft image loaded, fetch up to two bundled images
    // from the make_model directory (CE-550-550 has 3 → we take 2). Prefer
    // this over one duplicated card because the bundle has real variety.
    if (validMedia.length === 0) {
      const bundledUrls = getAircraftBundledCarouselUrls(makeModel).slice(0, 2);
      const bundledFetched = await Promise.all(
        bundledUrls.map((u) => fetchImageAsBase64(u, { maxWidth: 600, jpegQuality: 0.7 })),
      );
      bundledFetched.filter(Boolean).forEach((img) => validMedia.push(img));
    }
    const aircraftImages = validMedia;

    const titleParts = [
      data.aircraft?.tailNumber || '',
      data.aircraft?.name || '',
    ].filter(Boolean);
    const aircraftMedia = (mapImage || aircraftImages.length > 0)
      ? {
          title: titleParts.join(' · '),
          mapImage,
          aircraftImages,
        }
      : null;

    return {
      ...data,
      aircraft: { ...data.aircraft, image: aircraftImage },
      operatorLogo: logoSrc,
      aircraftMedia,
      legs: (data.legs || []).map((leg) => ({
        ...leg,
        passengers: (leg.passengers || []).map((p) =>
          typeof p === "object" ? p.fullName || "" : p
        ),
      })),
    };
  }, [fetchImageAsBase64, organization]);

  const renameWithClientId = (clientId, file) => {
    const client = clientId || 'unknown';
    const newName = `${client}_${file.name}`;
    return new File([file], newName, { type: file.type });
  };

  /** Save/update client passengers and link them to the quote */
  const savePassengers = useCallback(async () => {
    const clientId = quoteData?.client?.client_id || quoteData?.client?.id;
    if (!clientId || !passengers?.length) return;

    const seen = new Set();
    const uniquePassengers = passengers.filter((p) => {
      const key = typeof p === "string" ? p : p.fullName;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (!uniquePassengers.length) return;

    // Split into existing (already in DB) and new passengers
    const existingPassengers = uniquePassengers.filter((p) => typeof p !== "string" && p.clientPassengerId);
    const newPassengers = uniquePassengers.filter((p) => typeof p === "string" || !p.clientPassengerId);

    // Update existing passengers with any form changes
    if (existingPassengers.length) {
      await Promise.all(
        existingPassengers.map(async (p) => {
          let passport_url = p.existingPassportUrl || null;
          let visa_url = p.existingVisaUrl || null;
          if (p.passportFiles?.length) {

            //const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.passportFiles[0]), "passport");

            // file info
            const file = p.passportFiles[0];
            const key = `passports/${clientId}_${file.name}`;

            // get s3 signed urls
            const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
            
            // upload file to s3 and return public url
            passport_url = await apiClient.uploadFileToS3(file, s3Urls);
          
          }
          if (p.visaFiles?.length) {
            //const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.visaFiles[0]), "visa");

            // file info
            const file = p.visaFiles[0];
            const key = `visas/${clientId}_${file.name}`;

            // get s3 signed urls
            const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
            
            // upload file to s3 and return public url
            visa_url = await apiClient.uploadFileToS3(file, s3Urls);
          }
          let dob = null;
          if (p.dateOfBirth) {
            const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
          }
          return apiClient.updateClientPassenger(p.clientPassengerId, {
            full_name: p.fullName || "",
            date_of_birth: dob,
            nationality: p.nationality || null,
            passport_num: null,
            passport_url,
            visa_url,
            weight: p.weight || null,
            additional_info: {
              dietary_catering: p.dietaryCatering || null,
              cabin_environment: p.cabinEnvironment || null,
              medical_mobility: p.medicalMobility || null,
              pet_accommodations: p.petAccommodations || null,
            },
          });
        })
      );
    }

    // Only create new passengers via batch API
    let createdPassengers = [];
    if (newPassengers.length) {
      const newPayload = await Promise.all(
        newPassengers.map(async (p) => {
          if (typeof p === "string") {
            return { full_name: p };
          }
          let passport_url = p.existingPassportUrl || null;
          let visa_url = p.existingVisaUrl || null;
          if (p.passportFiles?.length) {
            //const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.passportFiles[0]), "passport");
            // file info
            const file = p.passportFiles[0];
            const key = `passports/${clientId}_${file.name}`;

            // get s3 signed urls
            const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
            
            // upload file to s3 and return public url
            passport_url = await apiClient.uploadFileToS3(file, s3Urls);
          }
          if (p.visaFiles?.length) {
            //const uploaded = await apiClient.uploadFile(renameWithClientId(clientId, p.visaFiles[0]), "visa");
            // file info
            const file = p.visaFiles[0];
            const key = `visas/${clientId}_${file.name}`;

            // get s3 signed urls
            const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
            
            // upload file to s3 and return public url
            visa_url = await apiClient.uploadFileToS3(file, s3Urls);
          }
          let dob = null;
          if (p.dateOfBirth) {
            const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
          }
          return {
            full_name: p.fullName || "",
            date_of_birth: dob,
            nationality: p.nationality || null,
            passport_num: null,
            passport_url,
            visa_url,
            weight: p.weight || null,
            additional_info: {
              dietary_catering: p.dietaryCatering || null,
              cabin_environment: p.cabinEnvironment || null,
              medical_mobility: p.medicalMobility || null,
              pet_accommodations: p.petAccommodations || null,
            }
          };
        })
      );
      const result = await apiClient.createClientPassengersBatch(clientId, newPayload);
      createdPassengers = Array.isArray(result) ? result : (result?.data || []);
    }

    // Build quote passengers payload from both existing and newly created
    const qId = quoteData?.quote_id || quoteData?.id;
    if (qId) {
      const quotePassengersPayload = [
        // Existing passengers without quotePassengerId — not yet in quote_passengers
        ...existingPassengers.filter((p) => !p.quotePassengerId).map((p) => {
          let dob = null;
          if (p.dateOfBirth) {
            const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
          }
          return {
            passenger_name: p.fullName || '',
            email: null,
            phone: null,
            passport_num: null,
            nationality: p.nationality || null,
            birth_date: dob,
            client_passenger_id: p.clientPassengerId,
          };
        }),
        // Newly created passengers — use IDs from batch response
        ...createdPassengers.map((cp, i) => ({
          passenger_name: cp.full_name || newPassengers[i]?.fullName || (typeof newPassengers[i] === "string" ? newPassengers[i] : ''),
          email: null,
          phone: null,
          passport_num: null,
          nationality: cp.nationality || null,
          birth_date: cp.date_of_birth || null,
          client_passenger_id: cp.client_passenger_id || null,
        })),
      ];
      if (quotePassengersPayload.length) {
        await apiClient.createQuotePassengersBatch(qId, quotePassengersPayload);
      }
    }
  }, [quoteData, passengers, renameWithClientId]);

  /**
   * Merge a patch into quote.files WITHOUT dropping sibling keys. The backend
   * stores `files` as a single JSON blob and REPLACES it on write, so a partial
   * `{ files: {...} }` would delete everything else (tripsheet, contract, etc.).
   * We re-read the latest files immediately before writing and spread the patch
   * on top, so concurrent/earlier saves in the same flow aren't clobbered.
   */
  const saveQuote = useCallback(async () => {
    if (!documentData) return;
    const clientId = quoteData?.client?.client_id || quoteData?.client?.id;
    const quoteId = quoteData?.quote_id || quoteData?.id;

    const promises = [];

    const passengerCount = Number(documentData.aircraft?.passengerCount) || 0;
    if (quoteId) {
      const moment = require('moment-timezone');
      const firstLeg = documentData.legs?.[0];
      const lastLeg = documentData.legs?.[documentData.legs.length - 1];

      const depCode = firstLeg?.departure?.code || '';
      const arrCode = lastLeg?.arrival?.code || '';

      let departureIso = null;
      if (firstLeg?.departure?.date && firstLeg?.departure?.time) {
        const tz = getAirportTimezone(depCode);
        const parsed = moment.tz(
          `${firstLeg.departure.date} ${firstLeg.departure.time}`,
          ['MMM D, YYYY HH:mm', 'MMMM D, YYYY HH:mm'],
          tz,
        );
        if (parsed.isValid()) departureIso = parsed.toISOString();
      }

      const quoteUpdate = {};
      if (passengerCount) quoteUpdate.passenger_count = passengerCount;
      if (depCode) quoteUpdate.departure_airport = depCode;
      if (arrCode) quoteUpdate.arrival_airport = arrCode;
      if (departureIso) quoteUpdate.departure_date = departureIso;

      if (Object.keys(quoteUpdate).length) {
        promises.push(apiClient.updateQuote(quoteId, quoteUpdate));
      }

      const bidId = acceptedBid?.bid_id;
      if (bidId) {
        const updatedLegs = buildUpdatedFlightsFromDocument(documentData, flights);
        promises.push(apiClient.updateBid(bidId, { leg_itinerary: updatedLegs }));
      }
    }

    await Promise.allSettled(promises);
  }, [documentData, quoteData, flights, acceptedBid]);

  // Local wrapper delegates to the shared service so the merge-safe pattern
  // is the same everywhere (useQuoteProcessor, QuoteDetailsDrawer, etc.).
  const patchQuoteFiles = useCallback(
    (quoteId, filesPatch, extra = {}) => patchQuoteFilesSvc(quoteId, filesPatch, extra),
    [],
  );

  /** Persist trip-sheet crew, additional services and FBOs onto quote.files */
  const saveCrewsAndServices = useCallback(async () => {
    const quoteId = quoteData?.quote_id || quoteData?.id;
    if (!quoteId) return;

    const crews = (crew || []).map((c) => ({
      name: c.name || "",
      role: c.role || "",
      phone: c.phone || "",
    }));
    const additional_services = (services || []).map((s) => ({
      name: s.name || "",
      details: s.details || "",
    }));

    // Per-leg FBO selections — store the compact ref (id + name + address + phone)
    // for each side. When an FBO has no address of its own, fall back to the
    // leg's airport address (already fetched into documentData via
    // getAirportByIcao), so no extra API call is needed.
    const withLegAddress = (ref, legIdx, side) => {
      if (!ref) return null;
      if (ref.address) return ref;
      const legAddr = documentData?.legs?.[legIdx]?.[side]?.address || '';
      return { ...ref, address: legAddr };
    };
    const fbo = (fbos || []).map((leg, idx) => ({
      departure: withLegAddress(leg?.departure, idx, 'departure'),
      arrival: withLegAddress(leg?.arrival, idx, 'arrival'),
    }));

    try {
      await patchQuoteFiles(quoteId, {
        crews,
        additional_services,
        fbo,
        request_client_passengers: requestClientPassengers,
      });
    } catch (err) {
      console.error('saveCrewsAndServices: updateQuote(files) failed', {
        backendDetail: err?.details,
        backendStatus: err?.status,
        err,
      });
    }
  }, [quoteData, crew, services, fbos, documentData, requestClientPassengers, patchQuoteFiles]);

  const upsertFlights = useCallback(async (updatedFlights, quoteId) => {
    if (!updatedFlights.length || !quoteId) return;
    const aircraftId = quoteData?.acceptedOption?.aircraft_id;

    const existingFlightsRaw = await apiClient.getFlights({ quote_id: quoteId });
    const existing = Array.isArray(existingFlightsRaw) ? existingFlightsRaw : [];

    const legKey = (f) =>
      `${(f.departure_airport || '').toUpperCase()}|${(f.arrival_airport || '').toUpperCase()}|${
        f.departure_date ? new Date(f.departure_date).getTime() : ''
      }`;
    const existingKeys = new Set(existing.map(legKey));

    const buildPayload = (f) => ({
      quote_id: quoteId,
      aircraft_id: aircraftId,
      flight_status: 'PENDING',
      departure_airport: f.departure_airport,
      arrival_airport: f.arrival_airport,
      departure_date: f.departure_date,
      arrival_date: null,
    });

    if (!hasFlight || existing.length === 0) {
      const toCreate = updatedFlights.filter((f) => !existingKeys.has(legKey(f)));
      if (toCreate.length) {
        await apiClient.createFlightsBatch(toCreate.map(buildPayload));
      }
    } else {
      const existingSorted = [...existing].sort((a, b) => {
        const ad = a.departure_date ? new Date(a.departure_date).getTime() : 0;
        const bd = b.departure_date ? new Date(b.departure_date).getTime() : 0;
        return ad - bd;
      });

      const ops = [];
      const overlap = Math.min(existingSorted.length, updatedFlights.length);
      for (let i = 0; i < overlap; i++) {
        const flightId = existingSorted[i].flight_id || existingSorted[i].id;
        if (!flightId) continue;
        const f = updatedFlights[i];
        ops.push(
          apiClient.patchFlight(flightId, {
            aircraft_id: aircraftId,
            departure_airport: f.departure_airport,
            arrival_airport: f.arrival_airport,
            departure_date: f.departure_date,
            arrival_date: null,
          })
        );
      }
      if (ops.length) await Promise.all(ops);

      const extras = updatedFlights.slice(existingSorted.length);
      const toCreate = extras.filter((f) => !existingKeys.has(legKey(f)));
      if (toCreate.length) {
        await apiClient.createFlightsBatch(toCreate.map(buildPayload));
      }
    }
  }, [quoteData, hasFlight]);

  const handleDownload = useCallback(async () => {
    if (!documentData || isDownloading) return;
    setIsDownloading(true);
    try {
      const pdfData = await resolvePdfData(documentData);
      const blob = await pdf(<TripSheetPdf data={pdfData} brandColor={organization?.branding?.color} />).toBlob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Trip_Sheet_${documentData.client.name || "Client"}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Failed to generate trip sheet PDF:", err);
    } finally {
      setIsDownloading(false);
    }
  }, [documentData, isDownloading, resolvePdfData]);


  const handleSend = useCallback(async () => {

    if (!documentData || isSending) return;
    const clientEmail = documentData.client.email || quoteData?.client?.email;
    if (!clientEmail) {
       toast.error("No client email address available.");
      return;
    }
    setIsSending(true);
    try {

      // save client info and quote fields
      await saveQuote();

      // save passengers
      await savePassengers();

      // save crews & additional services
      await saveCrewsAndServices();

      const pdfData = await resolvePdfData(documentData);
      const blob = await pdf(<TripSheetPdf data={pdfData} brandColor={organization?.branding?.color} />).toBlob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const clientName = documentData.client.name || "Client";

      const isRevised = Boolean(quoteData?.files?.tripsheet);
      const emailTitle = isRevised ? 'Revised Trip Sheet' : 'Booking Secured';

      // Email should reflect the edited document, same as the flights batch below.
      const updatedFlights = buildUpdatedFlightsFromDocument(documentData, flights);

      const emailHtml = await buildBookingSecuredEmailHtml({
        quote: quoteData,
        passengers: passengers,
        acceptedOption: acceptedBid,
        flights: updatedFlights,
        organizationName: organization?.name || '',
        organization,
        title: emailTitle,
      });

      await apiClient.sendMessage({
        From: "tools@trustjet.ai",
        To: clientEmail,
        Cc: userProfile?.email || '',
        ReplyTo: userProfile?.email || '',
        Subject: `${emailTitle} – ${tripTitle}`,
        HtmlBody: emailHtml,
        Attachments: [
          {
            Name: `Trip_Sheet_${clientName}.pdf`,
            Content: base64,
            ContentType: "application/pdf",
          },
        ],
      });

      // Upload trip sheet PDF and save to quote record
      const quoteId = quoteData?.quote_id || quoteData?.id;
      const pdfFile = new File([blob], `Trip_Sheet_${clientName}.pdf`, { type: "application/pdf" });

      // const renamed = new File([pdfFile], `tripsheet_${quoteId}_${pdfFile.name}`, { type: pdfFile.type });
      // const uploaded = await apiClient.uploadFile(renamed, "tripsheet");

      // file info
      const file = pdfFile;
      const key = `tripsheets/tripsheet_${quoteId}_${file.name}`;

      // get s3 signed urls
      const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
      
      // upload file to s3 and return public url
      const sheetUrl = await apiClient.uploadFileToS3(file, s3Urls) || "";

      if (quoteId) {
        // Merge so the tripsheet URL doesn't wipe crews/services/fbo just saved.
        // If we're approving a JB submission (source was TRIPSHEET_PENDING_APPROVAL),
        // also stamp the tripsheet_approval audit so we know who approved and when.
        const filesPatch = { tripsheet: sheetUrl };
        if ((quoteData?.status || '').toUpperCase() === 'TRIPSHEET_PENDING_APPROVAL') {
          filesPatch.tripsheet_approval = {
            approved_by: userProfile?.userprofile_id,
            approved_at: new Date().toISOString(),
          };
        }
        await patchQuoteFiles(quoteId, filesPatch, { status: "WAITING_FLIGHT" });
        await upsertFlights(updatedFlights, quoteId);
      }

      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success(`Trip sheet sent to ${clientEmail}`);
      onClose();
      setTimeout(() => navigate("/quotes"), 300);
    } catch (err) {
      console.error("Failed to send trip sheet:", err);
      toast.error("Failed to send trip sheet. Please try again.");
    } finally {
      setIsSending(false);
    }
  }, [documentData, isSending, quoteData, tripTitle, organization, userProfile, resolvePdfData, onClose, navigate, savePassengers, saveCrewsAndServices, upsertFlights, patchQuoteFiles]);

  // JB "Submit for Approval" — persists all drawer edits (client, passengers,
  // crew, services, FBOs, flight legs) and transitions the quote to
  // TRIPSHEET_PENDING_APPROVAL. No PDF, no S3 upload, no client email —
  // Admin reviews via the Pending Approvals card and sends the trip sheet
  // themselves. Covers both initial submissions (from ACCEPTED) and
  // revisions to an already-sent trip sheet (from PASSENGERS_PENDING /
  // WAITING_FLIGHT), routing every JB change back through admin review.
  const handleSaveDraft = useCallback(async () => {
    if (!documentData || isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      await saveQuote();
      await savePassengers();
      await saveCrewsAndServices();
      const updatedFlights = buildUpdatedFlightsFromDocument(documentData, flights);
      const quoteId = quoteData?.quote_id || quoteData?.id;
      if (quoteId) {
        await patchQuoteFiles(quoteId, {}, { status: 'TRIPSHEET_PENDING_APPROVAL' });
        await upsertFlights(updatedFlights, quoteId);
      }
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Submitted for approval.');
      onClose();
    } catch (err) {
      console.error('Failed to submit for approval:', err);
      toast.error('Failed to submit for approval. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  }, [documentData, isSavingDraft, flights, quoteData, saveQuote, savePassengers, saveCrewsAndServices, upsertFlights, patchQuoteFiles, queryClient, onClose]);


  // send request to client to add passenger details
  const handleRequestSend = async() => {

    setIsSending(true);

    // save client info and quote fields
    await saveQuote();

    // save passengers
    await savePassengers();

    // save crews & additional services
    await saveCrewsAndServices();

    const requestToken = await apiClient.createCheckoutToken({
          "quote_id": quoteData.quote_id,
          "amount": 1,
          "currency": "USD",
          "payment_method": "CREDIT_CARD",
          "description": "Charter flight quote payment",
          "metadata": {
              "customer_name": "John Doe",
              "customer_email": "john@example.com"
          }
      });
    
      const requestLink = `${window.location.origin}/tripsheet/${quoteData.quote_id}?token=${requestToken?.token}`;

      const updatedFlights = buildUpdatedFlightsFromDocument(documentData, flights);

      const finalEmailMessage = await buildFinalizePassengerProfilesEmailHtml({
        quote: quoteData,
        profileUrl: { url: requestLink },
        flights: updatedFlights,
        organization,
      });

      // Send message/email to client for adding passenger details
      const messagePayload = {
        From: "tools@trustjet.ai",
        To: quoteData.client?.clientData?.email || quoteData.client?.email,
        Cc: userProfile?.email || '',
        ReplyTo: userProfile?.email || '',
        Subject: `Passenger Request - (${tripTitle})`,
        HtmlBody: finalEmailMessage,
        Attachments: []
      };

      await apiClient.sendMessage(messagePayload);

      await upsertFlights(updatedFlights, quoteData.quote_id);

      // Merge against the freshest files (saveCrewsAndServices ran just above and
      // the cached quoteData.files is now stale) so we don't revert crews/fbo.
      await patchQuoteFiles(quoteData.quote_id, { request_client_passengers: 1 }, { status: 'PASSENGERS_PENDING' });

      setIsSending(false);
      onClose();

      toast.success(`Trip Sheet saved as draft.`, { position: 'bottom-right' });

      setTimeout(() => navigate("/dashboard"), 300);
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent side="right" className="w-full sm:max-w-5xl p-0 bg-gray-50 flex flex-col !gap-0">
        {/* Header */}
        <DrawerHeader className="flex flex-row items-center justify-between py-3 px-4 sm:px-5 space-y-0 flex-shrink-0">
          <div className="min-w-0">
            <DrawerTitle className="truncate">{tripTitle}</DrawerTitle>
            <DrawerDescription className="truncate">Trip Sheet for {clientName || "Client"}</DrawerDescription>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors bg-gray-100 px-2 py-1 rounded-md">
              <RiEyeLine className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
              aria-label="Close drawer"
            >
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
        </DrawerHeader>

        {/* Content - scrollable document preview */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 md:p-4 pt-0">
          {documentData && (
              <TripSheetDocument
                data={documentData}
                organization={organization}
                isOrgLoading={false}
                onChange={handleChange}
                onAddPassenger={handleAddPassenger}
                onRemovePassenger={handleRemovePassenger}
                onAddCrew={handleAddCrew}
                onRemoveCrew={handleRemoveCrew}
                onAddService={handleAddService}
                onRemoveService={handleRemoveService}
                protectedCounts={protectedCounts}
                readOnlyAircraft
                fetchingArrivalTimes={fetchingArrivalTimes}
              />
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-row items-center gap-2 sm:gap-3 p-3 pb-20 sm:p-4 sm:pb-4 bg-white border-t border-gray-200 flex-shrink-0">
          {requestClientPassengers && requestClientPassengers==1 ? (
            <button
              onClick={handleRequestSend}
              disabled={isSending || !documentData || fetchingArrivalTimes || fetchingAirportData}
              className="flex-1 flex items-center font-medium justify-center gap-1.5 py-2.5 sm:py-3 border text-xs sm:text-sm rounded-full transition-colors bg-primary-50 border-primary-50 text-primary-500"
            >
              <RiSaveLine className="w-4 h-4" />
              {isSending ? "Sending..." : (fetchingArrivalTimes || fetchingAirportData) ? "Loading..." : "Save Draft & Send Request to Client"}
            </button>
          ) : (<>
          <button
              onClick={handleDownload}
              disabled={isDownloading || !documentData || fetchingArrivalTimes || fetchingAirportData}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 sm:py-3 bg-white border border-gray-200 text-gray-800 text-xs sm:text-sm rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiDownloadLine className="w-4 h-4" />
              {isDownloading
                ? "Generating..."
                : (fetchingArrivalTimes || fetchingAirportData)
                  ? "Loading..."
                  : "Download"}
          </button>
          {canSaveDraftOnly && (
            <button
              onClick={handleSaveDraft}
              disabled={isSavingDraft || !documentData || fetchingArrivalTimes || fetchingAirportData}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 sm:py-3 border font-medium bg-primary-50 border-primary-50 text-primary-500 text-xs sm:text-sm rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiHourglass2Line className="w-4 h-4" />
              {isSavingDraft
                ? "Submitting..."
                : (fetchingArrivalTimes || fetchingAirportData)
                  ? "Loading..."
                  : "Submit for Approval"}
            </button>
          )}
          {canSendTripSheet && (
            <button
              onClick={handleSend}
              disabled={isSending || !documentData || fetchingArrivalTimes || fetchingAirportData}
              className="flex-1 flex text-white items-center justify-center gap-1.5 py-2.5 sm:py-3 bg-primary-700 text-xs sm:text-sm rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiSendPlane2Line className="w-4 h-4" />
              {isSending
                ? "Sending..."
                : (fetchingArrivalTimes || fetchingAirportData)
                  ? "Loading..."
                  : "Send Trip Sheet"}
            </button>
          )}</>)}

        </div>
      </DrawerContent>

    </Drawer>
  );
};

export default TripSheetPreviewDrawer;